import {
  AidRequestForm,
  Association,
  Category,
  Community,
  Group,
  Opportunity,
  OrganizationManager,
  Population,
  Position,
  Status,
  Team,
} from "@entities";
import { extender } from "@shared/backend/class-extender";
import HyperEntity from "@shared/backend/hyper-entity.decorator";
import { JsonField } from "@shared/helpers/custom-fields";
import { Roles } from "@shared/Roles";
import { Fields, Relations } from "remult";
import { OrganizationEntityConfig } from "./config";
import { OrganizationFilters } from "./filters";
import { OrganizationMethods } from "./methods";

@HyperEntity("organization", OrganizationEntityConfig)
export class Organization extends extender(
  OrganizationFilters,
  OrganizationMethods,
) {
  @Fields.uuid({ allowApiUpdate: false })
  id = "";

  @Relations.toMany(() => Association, "organizationId")
  associations?: Association[];

  @Relations.toMany(() => AidRequestForm, "organization")
  forms?: AidRequestForm[];

  @Relations.toMany(() => Position, "organization")
  organizationPositions?: Position[];

  @Relations.toMany(() => Team, "organization")
  teams?: Team[];

  @Relations.toMany(() => OrganizationManager, "organization")
  managers: OrganizationManager[] = [];

  @Relations.toMany(() => Community, "organizationId")
  communities?: Community[];

  @Relations.toMany(() => Group, "organization")
  groups?: Group[];

  @Relations.toMany(() => Status, "organization")
  status?: Status[];

  @Relations.toMany(() => Opportunity)
  opportunities: Opportunity[] = [];

  @Relations.toMany<Organization, Category>(() => Category, "organization")
  categories: Category[] = [];

  @Relations.toMany<Organization, Population>(() => Population, "organization")
  populations: Population[] = [];

  @Fields.string()
  name = "";

  @JsonField<Organization, Organization["positionsWithCategory"]>({
    allowedKeys: ["category", "id", "name"],
    includeInApi: true,
    dbName: "positions_with_category",
  })
  positionsWithCategory: { id: string; name: string; category: string; }[] = [];

  @JsonField<Organization, Organization["requestTypesWithCategory"]>({
    allowedKeys: ["category", "id", "name"],
    includeInApi: true,
    dbName: "request_types_with_category",
  })
  requestTypesWithCategory: { id: string; name: string; category: string; }[] =
    [];

  @Fields.string({
    dbName: "default_opportunity_background_image_id",
    includeInApi: Roles.AdminsAndManager,
  })
  defaultOpportunityBackgroundImageId = "";

  @Fields.boolean({
    dbName: "is_required_id",
  })
  isRequiredId = false;

  @Fields.boolean({
    dbName: "is_required_email",
  })
  isRequiredEmail = false;

  @Fields.boolean({
    dbName: "has_default_report",
    includeInApi: Roles.AdminsAndManager,
  })
  hasDefaultReport = false;

  @Fields.boolean({ dbName: "hide_skill_questionnaire" })
  hideSkillQuestionnaire = false;

  @Fields.boolean({ dbName: "is_accept_request" })
  isAcceptRequest = false;

  @Fields.string({ dbName: "organization_image_id" })
  organizationImageId = "33b0cfec-11a3-4b34-ba81-b12bfce4e931";

  @Fields.literal(
    () =>
      [
        "PREMIUM",
        "MARKETPLACE",
        "COMMAND_AND_CONTROL_CENTER",
        "NONPROFIT_ORGANIZATION",
      ] as const,
    {
      dbName: "organization_type",
      includeInApi: true,
      allowApiUpdate: false,
      allowNull: true,
    },
  )
  organizationType:
    | "PREMIUM"
    | "MARKETPLACE"
    | "COMMAND_AND_CONTROL_CENTER"
    | "NONPROFIT_ORGANIZATION"
    | null = null;

  @Fields.string({
    dbName: "hagai_username",
    includeInApi: Roles.AdminsAndManager,
  })
  hagaiUsername = "";

  @Fields.string({ dbName: "setup_description" })
  setupDescription = "";

  @Fields.string({
    dbName: "request_description",
    defaultValue: () =>
      "היי! אנחנו כאן כדי לטפל בבקשות שלכם ולסייע בכל מה שניתן",
  })
  requestDescription = "";

  @Fields.string({
    dbName: "request_notes",
  })
  requestNotes = "";

  @Fields.boolean({
    dbName: "has_hours_reporting",
  })
  hasHoursReporting = false;

  @Fields.createdAt({ dbName: "created_at" })
  createdAt = new Date();

  @Fields.updatedAt({ dbName: "updated_at" })
  updatedAt = new Date();
}
